import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSSProperties, useEffect, useState } from 'react';
import { useAppSelector } from 'app/hooks';
import { OrgShippingCarrier } from 'domains/orgDomain';
import { CSS } from '@dnd-kit/utilities';
import { ProjectJobDragIcon } from 'components/Icons';
import {
  CarrierTypeEnum,
  ShippingMethodNonstandardTypeEnum,
} from 'domains/shippingDomain';
import { DEBOUNCE_TIME } from 'utils/constants';
import shippingService from 'services/shippingService';
import { useDebounceFn } from 'ahooks';
import { useOrgId } from 'hooks/useOrg';
import RevxFlexLayout from 'components/RevxFlexLayout';

const { Nonstandard } = CarrierTypeEnum;

const ManageDisplay = () => {
  const [items, setItems] = useState<OrgShippingCarrier[]>([]);

  const orgId = useOrgId();
  const { orgShippingConfig } = useAppSelector((state) => state.shipping);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );
  const { run: saveSorting } = useDebounceFn(
    async (sort: string) => {
      await shippingService.saveCarrierSorting(orgId, sort);
    },
    { wait: DEBOUNCE_TIME }
  );

  useEffect(() => {
    if (orgShippingConfig?.carriers) {
      setItems(orgShippingConfig.carriers);
    }
  }, [orgShippingConfig]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex(
        (item) => item.serialNumber === active.id
      );
      const newIndex = items.findIndex(
        (item) => item.serialNumber === over?.id
      );
      const updatedItems = arrayMove(items, oldIndex, newIndex);
      setItems(updatedItems);
      await saveSorting(
        updatedItems.map((item) => item.serialNumber).join(',')
      );
    }
  };

  return (
    <div className="manage-display">
      <div className="manage-display__title">
        Order the carriers and options:
      </div>
      <div className="manage-display__content">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items.map((item) => item.serialNumber)}>
            {items
              .filter(
                (i) =>
                  !(
                    i.carrierType === Nonstandard &&
                    i.nonstandard?.nonstandardType ===
                      ShippingMethodNonstandardTypeEnum.Internal_Distribution
                  )
              )
              .map((item) => (
                <SortableItem key={item.serialNumber} item={item} />
              ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

const SortableItem = ({ item }: { item: OrgShippingCarrier }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.serialNumber });
  const { carrierType, nonstandard } = item;
  const sortableItemDraggingStyles: CSSProperties = isDragging
    ? {
        zIndex: 999,
        position: 'relative',
      }
    : {};
  let carrierName: string;
  if (carrierType !== Nonstandard) {
    carrierName = carrierType;
  } else {
    carrierName = nonstandard!.optionName;
  }
  return (
    <div
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        ...sortableItemDraggingStyles,
      }}
      className="carrier-item"
    >
      <div {...attributes} style={{ display: 'flex', alignItems: 'center' }}>
        <RevxFlexLayout className="carrier-item__label">
          <div ref={setNodeRef} {...listeners} className="drag-btn click">
            <ProjectJobDragIcon />
          </div>
          <div>{carrierName}</div>
        </RevxFlexLayout>
        {/*<NormalCarrierOption item={item} />*/}
      </div>
    </div>
  );
};

/*const NormalCarrierOption = ({ item }: { item: OrgShippingCarrier }) => {
  const orgId = useOrgId();
  const { carrierType, ups, fedEx } = item;
  if (carrierType === Nonstandard) {
    return null;
  }
  const defaultVal = () => {
    const carrierMapping = {
      [UPS]: ups?.serviceDisplayOrder,
      [FedEx]: fedEx?.serviceDisplayOrder,
    };
    return carrierMapping[carrierType as typeof UPS | typeof FedEx];
  };
  const normalCarrierChangeHandler = async ({
    target: { value: serviceDisplayOrder },
  }: RadioChangeEvent) => {
    const mergedItem =
      carrierType === UPS
        ? { ups: { serviceDisplayOrder } }
        : { fedEx: { serviceDisplayOrder } };
    const updatedItem = { ...item, ...mergedItem } as OrgShippingCarrier;
    await shippingService.updateOrgShippingConfigCarrier(orgId, updatedItem);
  };

  return (
    <div className="normal-carrier">
      <Radio.Group
        defaultValue={defaultVal}
        onChange={normalCarrierChangeHandler}
      >
        <Radio value={LOWEST_TO_HIGHEST_PRICE}>
          Display lowest to highest price options
        </Radio>
        <Radio value={FASTEST_TO_LONGEST_DELIVERY_DATE}>
          Display fastest to longest delivery date
        </Radio>
      </Radio.Group>
    </div>
  );
};*/

export default ManageDisplay;
